import { FC, ReactNode } from "react";
import { CheckIcon } from "@heroicons/react/24/outline";
import { formatPhone } from "utils/formatPhone";
import CarPhoto from "./CarPhoto";
import type { Stop } from "./tripStops";

const Box: FC<{ title: string; onEdit?: () => void; children: ReactNode; className?: string }> = ({
  title,
  onEdit,
  children,
  className = "",
}) => (
  <section className={`rounded-2xl border border-secondary-100 bg-card-color p-3 ${className}`}>
    {/* The row keeps the Edit button's height without it (1NOW-11218), so every box spaces its title alike. */}
    <div className="flex min-h-[44px] items-center justify-between gap-3">
      <h3 className="text-sm font-extrabold">{title}</h3>
      {onEdit && (
        <button
          type="button"
          onClick={onEdit}
          aria-label={`Edit ${title.toLowerCase()}`}
          className="-mr-2 min-h-[44px] px-2 text-xs font-bold underline"
        >
          Edit
        </button>
      )}
    </div>
    <div className="-mt-1 text-sm">{children}</div>
  </section>
);

const Ticks: FC<{ lines: string[] }> = ({ lines }) => (
  <ul className="space-y-1">
    {lines.map((t, i) => (
      <li key={i} className="flex gap-2">
        <CheckIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="min-w-0 break-words">{t}</span>
      </li>
    ))}
  </ul>
);

/**
 * Step 4 of the booking: everything on one screen before going on, each
 * part with an Edit that goes back to where it was chosen (1NOW-11155). It
 * replaces the "Details" summary sheet. The agreement is accepted on the next
 * step, Pay: the filled-in agreement needs the booking this step's button
 * makes, so pay now or pay later is answered by the button itself ("Pay and
 * book now", or "Pay later" for a car that allows it), and the booking is
 * made that way.
 */
const ReviewStep: FC<{
  car: { name?: string; photo?: string | null };
  pickup: Stop;
  dropoff: Stop;
  /** The chosen protection package, as one line (with its deductible), or none. */
  protection: string | null;
  /** The add-ons and extras, one line each. */
  extras: string[];
  driver: { firstName: string; lastName: string; email: string; phone: string };
  onEditTrip: () => void;
  /** Each Edit goes to the step where that part was chosen (and comes back here from it). */
  onEditDriver: () => void;
  onEditProtection: () => void;
  /** None when the car has no Extras step (1NOW-11218). */
  onEditExtras?: () => void;
  /** Desktop (1NOW-11173): blocks side by side, and no car line (the overview card has the car). */
  wide?: boolean;
}> = ({
  car,
  pickup,
  dropoff,
  protection,
  extras,
  driver,
  onEditTrip,
  onEditDriver,
  onEditProtection,
  onEditExtras,
  wide,
}) => (
  <div className={wide ? "grid grid-cols-2 gap-4" : undefined}>
    {!wide && (
      <div className="flex items-center gap-3">
        <CarPhoto photo={car.photo} className="h-12 w-[72px] rounded-lg" />
        <b className="line-clamp-2 min-w-0 break-words" title={car.name}>
          {car.name}
        </b>
      </div>
    )}

    <Box title="Pickup and return" onEdit={onEditTrip} className={wide ? "col-span-2 !p-5" : "mt-3"}>
      {wide ? (
        <div className="grid grid-cols-2 gap-6">
          <div className="min-w-0">
            <span className="text-xs opacity-70">Pickup</span>
            <b className="block break-words">{pickup.place}</b>
            <span className="block">{pickup.when}</span>
          </div>
          <div className="min-w-0">
            <span className="text-xs opacity-70">Return</span>
            <b className="block break-words">{dropoff.place}</b>
            <span className="block">{dropoff.when}</span>
          </div>
        </div>
      ) : (
        <div className="flex gap-3">
          <div className="flex flex-col items-center py-1" aria-hidden="true">
            <span className="h-2.5 w-2.5 rounded-sm bg-text-color" />
            <span className="my-1 w-0.5 flex-1 bg-text-color" />
            <span className="h-2.5 w-2.5 rounded-sm bg-text-color" />
          </div>
          <div className="min-w-0">
            <span className="text-xs opacity-70">Pickup</span>
            <b className="block break-words">{pickup.place}</b>
            <span className="block">{pickup.when}</span>
            <span className="mt-2 block text-xs opacity-70">Return</span>
            <b className="block break-words">{dropoff.place}</b>
            <span className="block">{dropoff.when}</span>
          </div>
        </div>
      )}
    </Box>

    <Box title="Driver" onEdit={onEditDriver} className={wide ? "!p-5" : "mt-3"}>
      <b className="block break-words">
        {driver.firstName} {driver.lastName}
      </b>
      <span className="block break-all">{driver.email}</span>
      <span className="block">{formatPhone(driver.phone)}</span>
    </Box>

    <Box title="Protection" onEdit={onEditProtection} className={wide ? "!p-5" : "mt-3"}>
      {protection ? <Ticks lines={[protection]} /> : <span className="opacity-70">No protection chosen.</span>}
    </Box>

    <Box title="Extras" onEdit={onEditExtras} className={wide ? "col-span-2 !p-5" : "mt-3"}>
      {extras.length === 0 ? <span className="opacity-70">No extras added.</span> : <Ticks lines={extras} />}
    </Box>
  </div>
);

export default ReviewStep;
